import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

export type DocumentProcessingStatus = 
  | 'uploading' 
  | 'uploaded' 
  | 'extracting' 
  | 'chunking' 
  | 'indexing' 
  | 'ready' 
  | 'failed';

interface DocumentStatus {
  processing_status: DocumentProcessingStatus;
  processing_progress: number;
  processing_message: string;
}

export const useDocumentStatus = (documentId: string | null) => {
  const [status, setStatus] = useState<DocumentStatus>({
    processing_status: 'uploading',
    processing_progress: 0,
    processing_message: 'Uploading...'
  });
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!documentId) return;

    let channel: RealtimeChannel | null = null;

    const setupRealtimeSubscription = async () => {
      // 1. Fetch initial status
      try {
        const { data, error: fetchError } = await supabase
          .from('documents')
          .select('processing_status, processing_progress, processing_message')
          .eq('id', documentId)
          .maybeSingle();
        
        if (fetchError) {
          console.error('Error fetching initial document status:', fetchError);
        } else if (data) {
          // Data exists, check if it has the expected properties
          const docData = data as Record<string, any>;
          if ('processing_status' in docData) {
            setStatus({
              processing_status: docData.processing_status || 'uploading',
              processing_progress: docData.processing_progress || 0,
              processing_message: docData.processing_message || 'Processing...'
            });
            setIsComplete(
              docData.processing_status === 'ready' || 
              docData.processing_status === 'failed'
            );
          }
        }
      } catch (error) {
        console.error('Error fetching initial document status:', error);
      }

      // 2. Subscribe to realtime updates
      channel = supabase
        .channel(`document-${documentId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'documents',
            filter: `id=eq.${documentId}`
          },
          (payload) => {
            console.log('📡 Realtime document status update:', payload.new);
            
            const newStatus = payload.new as any;
            const statusUpdate: DocumentStatus = {
              processing_status: newStatus.processing_status || 'uploading',
              processing_progress: newStatus.processing_progress || 0,
              processing_message: newStatus.processing_message || 'Processing...'
            };
            
            setStatus(statusUpdate);
            
            // Mark as complete when ready or failed
            if (statusUpdate.processing_status === 'ready' || statusUpdate.processing_status === 'failed') {
              setIsComplete(true);
            }
          }
        )
        .subscribe();

      console.log(`✅ Subscribed to document status updates for ${documentId}`);
    };

    setupRealtimeSubscription();

    // 3. Cleanup on unmount
    return () => {
      if (channel) {
        channel.unsubscribe();
        console.log(`🔌 Unsubscribed from document ${documentId}`);
      }
    };
  }, [documentId]);

  return { status, isComplete };
};

